import { useEffect, useState, useContext } from "react";
import {
  Alert,
  Backdrop,
  Button,
  Box,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
  Link,
} from "@mui/material";
import BDatePicker from "../../input/BDatePicker";
import bkstApi from "../../../api";
import AddressAutocomplete from "../../gmaps/AddressAutocomplete";
import { getDeliveryProviders, getFormattedAddress } from "../../util";
import Price from "../Price";
import { UserContext } from "../../../context/UserContext";
import currency from "currency.js";
import TimeRangePicker from "../../input/TimeRangePicker";

export default function FulfillmentEntry(props) {
  const { user } = useContext(UserContext);
  const { fulfillment } = props;

  const [storeLocations, setStoreLocations] = useState([]);

  const [errors, setErrors] = useState([]);
  const [loading, setLoading] = useState(false);

  const getDeliveryQuote = () => {
    const arr = [];
    setErrors(arr);
    if (arr.length > 0) {
      return;
    } else {
      setLoading(true);
      bkstApi
        .post(`/my/delivery/quote`, {
          address: fulfillment.location,
          date: fulfillment.date,
          time: fulfillment.time,
          provider: { name: fulfillment?.provider?.name },
        })
        .then((res) => {
          props.onChange({
            ...fulfillment,
            option: {
              delivery: { amount: res.data.quote, includeFees: false, total: res.data.quote },
            },
          });
          setLoading(false);
        })
        .catch((err) => {
          setErrors([err.response?.data?.msg]);
          setLoading(false);
        });
    }
  };

  useEffect(() => {
    bkstApi(`/my/location`).then((res) => {
      setStoreLocations(res.data.locations);
    });
  }, []);

  const handleFulfillmentTypeChange = (newVal) => {
    // clear address and instructions
    let o = { ...fulfillment, type: newVal, address: "", location: "", instructions: "" };
    delete o.option;
    delete o.provider;
    delete o.storeLocationId;

    if (o?.type === "delivery") {
      o.provider = { name: user.config?.fulfillment?.delivery?.["third-party"] || "in-house" };
    }
    props.onChange(o);
    setErrors([]);
  };

  const handleLocationChange = (newVal, storeLocationId) => {
    let o = { ...fulfillment, location: newVal };
    delete o.option;
    delete o.provider;
    if (storeLocationId) {
      o.storeLocationId = storeLocationId;
    } else {
      delete o.storeLocationId;
    }
    props.onChange(o);
    setErrors([]);
  };

  const handleDateChange = (date) => {
    let o = { ...fulfillment, date };
    delete o.option;
    delete o.provider;
    props.onChange(o);
    setErrors([]);
  };

  const handleTimeChange = (time) => {
    let o = { ...fulfillment, time };
    delete o.option;
    delete o.provider;
    props.onChange(o);
    setErrors([]);
  };

  const DSPs = getDeliveryProviders(user.config?.fulfillment?.delivery);

  return (
    <Grid container spacing={3} sx={{ minWidth: 0 }}>
      <Grid item xs={12}>
        <RadioGroup
          row
          name="type"
          value={fulfillment?.type}
          onChange={(e) => handleFulfillmentTypeChange(e.target.value)}
        >
          <FormControlLabel
            value="pickup"
            control={<Radio />}
            label={`Pickup`}
            size="large"
            color="primary"
          />
          <FormControlLabel value="delivery" control={<Radio />} label={`Delivery`} />
        </RadioGroup>
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormControl variant="outlined" fullWidth>
          <BDatePicker
            label="Date"
            value={fulfillment?.date || ""}
            onChange={handleDateChange}
            disablePast
            required
            autoOk
          />
        </FormControl>
      </Grid>
      <Grid item sm={6} xs={12}>
        <TimeRangePicker
          time={fulfillment?.time || ""}
          onChange={({ from, to }) => {
            let range = `${from} - ${to}`;
            if (from === to) {
              range = from;
            }
            handleTimeChange(range);
          }}
        />
      </Grid>
      <Grid item xs={12} zeroMinWidth>
        {fulfillment?.type === "pickup" && (
          <FormControl
            variant="outlined"
            fullWidth
            required
            sx={{ minWidth: 0, maxWidth: "100%" }}
          >
            <InputLabel id="address-label">Pickup Location</InputLabel>
            <Select
              value={
                fulfillment?.location?.id ||
                storeLocations.find((o) => o.address?.street === fulfillment?.location?.street)
                  ?.id ||
                ""
              }
              labelId="address-label"
              label="Pickup Location"
              id="address"
              name="address"
              renderValue={(selectedId) => {
                const loc =
                  storeLocations.find((o) => o.id === selectedId) ||
                  storeLocations.find((o) => o.address?.street === fulfillment?.location?.street);
                return loc
                  ? getFormattedAddress(loc.address)
                  : getFormattedAddress(fulfillment?.location);
              }}
              onChange={(e) => {
                const loc = storeLocations.find((o) => o.id === e.target.value);
                handleLocationChange(loc ? { ...loc.address, id: loc.id } : "", loc?.id);
              }}
              sx={{
                width: "100%",
                maxWidth: "100%",
                "& .MuiOutlinedInput-root": {
                  minWidth: 0,
                  maxWidth: "100%",
                },
                "& .MuiSelect-select": {
                  minWidth: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                },
              }}
              MenuProps={{
                PaperProps: {
                  sx: { maxWidth: "calc(100vw - 32px)" },
                },
              }}
            >
              {storeLocations.map((o) => {
                return (
                  <MenuItem key={o.id} value={o.id} sx={{ whiteSpace: "normal" }}>
                    {getFormattedAddress(o.address)}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        )}
        {fulfillment?.type === "delivery" && (
          <FormControl variant="outlined" fullWidth sx={{ minWidth: 0, maxWidth: "100%" }}>
            <AddressAutocomplete
              value={fulfillment?.location || ""}
              onPlaceSelect={(loc) => {
                handleLocationChange(loc);
              }}
              label="Delivery Address"
              required
            />
          </FormControl>
        )}
      </Grid>
      {fulfillment?.type === "delivery" && (
        <Grid item xs={12}>
          <TextField
            id="instructions"
            label="Delivery Instructions"
            name="instructions"
            variant="outlined"
            fullWidth
            value={fulfillment?.instructions || ""}
            onChange={(e) => props.onChange({ ...fulfillment, instructions: e.target.value })}
            helperText="e.g. leave with concierge, call me when you arrive, etc."
          />
        </Grid>
      )}
      {errors && errors.length > 0 && (
        <Grid item sm={12} xs={12}>
          <Alert severity="error">{errors.join(". ")}</Alert>
        </Grid>
      )}
      {fulfillment?.type === "delivery" && (
        <Grid item sm={6} xs={12}>
          <FormControl variant="outlined" required fullWidth>
            <InputLabel id="delivery-option-label">Delivery Provider</InputLabel>
            <Select
              value={fulfillment?.provider?.name || ""}
              labelId="provider-label"
              label="Delivery Provider"
              id="provider"
              name="provider"
              onChange={(e) => {
                let o = { ...fulfillment };
                o.provider = { name: e.target.value };
                delete o.option;
                props.onChange(o);
              }}
            >
              {DSPs.map((o) => {
                return <MenuItem value={o.key}>{o.value.name}</MenuItem>;
              })}
            </Select>
          </FormControl>
        </Grid>
      )}
      {fulfillment?.type === "delivery" && (
        <Grid item xs={12} sm={6}>
          {fulfillment?.provider?.name === "in-house" ? (
            <Price
              value={
                fulfillment.option?.delivery || {
                  amount: "",
                  includeFees: user.config?.transferFees === "false" ? false : true,
                }
              }
              onChange={(o) => {
                props.onChange({ ...fulfillment, option: { delivery: o } });
              }}
            />
          ) : (
            <Box>
              {fulfillment.option?.delivery ? (
                <Box>
                  <Link
                    onClick={() => getDeliveryQuote()}
                    underline="never"
                    sx={{ cursor: "pointer" }}
                  >
                    <Box
                      px={2}
                      py={0.75}
                      sx={{ backgroundColor: "#DEDEDE", borderRadius: "5px", textAlign: "center" }}
                    >
                      <Typography variant="h5">
                        {currency(fulfillment.option.delivery.amount).format()}
                      </Typography>
                      <Box>
                        <Typography variant="caption" display="block">
                          Refresh Quote
                        </Typography>
                      </Box>
                    </Box>
                  </Link>
                </Box>
              ) : (
                <Button
                  color="primary"
                  variant="contained"
                  onClick={() => getDeliveryQuote()}
                  fullWidth
                  sx={{ height: "56px" }}
                  disabled={
                    !(
                      fulfillment?.location &&
                      fulfillment.date &&
                      fulfillment.time &&
                      fulfillment.location &&
                      fulfillment?.provider?.name
                    )
                  }
                >
                  Get Delivery Quote
                </Button>
              )}
            </Box>
          )}
        </Grid>
      )}
      {loading && (
        <Backdrop open={true} style={{ zIndex: "1000", color: "#fff" }}>
          <CircularProgress color="inherit" />
        </Backdrop>
      )}
    </Grid>
  );
}
